import type {DesignerStores} from '@packages/systems/designer/flux';
import {abilityFeature} from '../abilityFeature';

export type CollectionType = 'cms' | 'ecommerce';
export type ItemType = 'base-item' | 'inline-item';

const getCurrentCollectionTypes = (
  state: DesignerStores['CurrentCollectionStore']
): {
  parentCollectionType: CollectionType | null;
  collectionType: CollectionType;
  itemType: ItemType | null;
} | null => {
  if (!state.get('currentCollection')) {
    return null;
  }

  const currentItem = state.get('currentItem');
  const currentInlineItem = state.get('currentInlineItem');

  // When no item is open (e.g. Collection Settings), use the collection type from the current collection.
  if (!currentItem && !currentInlineItem) {
    return {
      parentCollectionType: null,
      collectionType: state.get('currentCollection')?.plugin as CollectionType,
      itemType: null,
    };
  }

  const currentItemType = currentInlineItem ? 'inline-item' : 'base-item';

  const currentCollectionType = currentInlineItem
    ? state.getIn(['currentInlineItem', 'collection', 'plugin'])
    : state.getIn(['currentCollection', 'plugin']);

  const parentCollectionType =
    currentItemType === 'inline-item'
      ? state.getIn(['currentCollection', 'plugin'])
      : null;

  return {
    parentCollectionType: parentCollectionType as CollectionType | null,
    collectionType: currentCollectionType as CollectionType,
    itemType: currentItemType,
  };
};

export const cms = abilityFeature({
  key: 'cms',
  isValid: function isCMSValid(state, config) {
    const currentCollectionTypes = getCurrentCollectionTypes(
      state.CurrentCollectionStore
    );

    if (!currentCollectionTypes) {
      return false;
    }

    // If the config doesn't require an item type, only check collection type.
    if (!config.itemType) {
      return Boolean(
        config.collectionType === currentCollectionTypes.collectionType
      );
    }

    // Config requires an item type but no item is open — not valid.
    if (!currentCollectionTypes.itemType) {
      return false;
    }

    // Only check for the parent collection type if it's not null
    if (!currentCollectionTypes.parentCollectionType) {
      return Boolean(
        config.collectionType === currentCollectionTypes.collectionType &&
        config?.itemType?.includes(currentCollectionTypes.itemType)
      );
    }

    return Boolean(
      config.collectionType === currentCollectionTypes.collectionType &&
      config.collectionType === currentCollectionTypes.parentCollectionType &&
      config?.itemType?.includes(currentCollectionTypes.itemType)
    );
  },
});
